<script setup lang="ts">
import CampaignPreviewTile from "~/components/AdvertiserLandingV2/CampaignPreviewTile.vue";
import Mission from "~/components/AdvertiserLandingV2/Mission.vue";
import PoweredByEvangelistDark from "./PoweredByEvangelistDark.vue";
import PoweredByEvangelistLight from "./PoweredByEvangelistLight.vue";
import { useQuery } from "@tanstack/vue-query";
import { useAdvertiserMetadata } from "~/composables/useAdvertiserMetadata";
import CustomUserBadge from "~/components/AdvertiserLandingV2/CustomUserBadge.vue";

const props = defineProps<{
  brandName: string;
  customUserName?: string;
  customUserProfilePicUrl?: string;
}>();

const route = useRoute();
const router = useRouter();
const { $trpc } = useNuxtApp();
const { advertiserMetadata } = useAdvertiserMetadata({
  advertiserName: props.brandName,
});

definePageMeta({
  layout: "blank",
});

const signWithX = async ({
  campaignId,
  campaignIndex,
}: {
  campaignId: string;
  campaignIndex: number;
}) => {
  try {
    trackEvent("user_sign_in_via_partner_page", {
      advertiser: props.brandName.toLowerCase(),
      trigger: "tile_active_campaign_clicked",
      domainType: "partner",
      page_path: document.location.pathname,
      deviceId: useAmplitudeStore().deviceId,
      referrer: document.referrer || "",
      campaignId: campaignId,
      campaignIndex: campaignIndex,
    });

    const config = useRuntimeConfig();
    const utms = checkForExistingUtms();
    const params = new URLSearchParams({
      signin_from_partner: props.brandName.toLowerCase(),
      ...(utms ?? {}),
    });
    const redirectUrl = `${config.public.CORE_SPA_DOMAIN}?${params.toString()}`;
    window.location.href = redirectUrl;
  } catch (error) {
    console.error(error);
  }
};

function checkAndSaveVtidInRoute() {
  if (typeof route.query?.vtid === "string") {
    localStorage.setItem("vtid", route.query.vtid);

    // Clear the route query params
    router.push({ query: {} });
  }
}

const { data: pageData, isSuccess: pageDataSuccess } = useQuery({
  queryKey: ["pageData", props.brandName.toLocaleLowerCase()],
  queryFn: () =>
    $trpc.getLandingPageData.query({
      theme: props.brandName.toLocaleLowerCase(),
    }),
});

const { data: campaigns, isSuccess: campaignsSuccess } = useQuery({
  queryKey: ["campaigns", props.brandName],
  queryFn: () =>
    $trpc.listLandingPageCampaigns.query({
      theme: props.brandName,
    }),
});

function trackEvents() {
  const vtid = localStorage.getItem("vtid");

  const urlParams = new URLSearchParams(window.location.search);

  const signupBonus = urlParams.get("signup_bonus");
  const signupBonusAmount = urlParams.get("signup_bonus_amount");
  const signupBonusCurrency = urlParams.get("signup_bonus_currency");

  const invited = urlParams.get("invited");
  const vip = urlParams.get("vip");

  trackEvent("page_landing_viewed", {
    advertiser: props.brandName.toLowerCase(),
    trigger: "page_load",
    vip: vip === "true",
    invited: invited === "true",
    active_campaign_count: campaigns.value?.length,
    campaigns: campaigns.value?.map((campaign) => ({
      id: campaign.campaign_week_id,
    })),
    signup_bonus: signupBonus || false,
    signup_bonus_amount: signupBonusAmount,
    signup_bonus_currency: signupBonusCurrency,
    ...(vtid && { walletHash: vtid }),
  });
}

const title = computed(() => {
  return pageData.value?.landing_page_seo_title
    ? pageData.value.landing_page_seo_title
    : `${pageData.value?.name} Evangelist Program`;
});

const description = computed(() => {
  return pageData.value?.landing_page_seo_description
    ? pageData.value.landing_page_seo_description
    : `Join the ${pageData.value?.name} Evangelist Program and earn rewards for creating content about your ${pageData.value?.name} experience on X.`;
});

useHead({
  title: title,
  meta: [
    {
      name: "description",
      content: description,
    },
  ],
});

function trackPreferredAdvertiser() {
  setUserPropertiesOnce({
    first_advertiser: props.brandName.toLowerCase(),
  });

  setUserProperties({
    advertiser: props.brandName.toLowerCase(),
  });
}

onMounted(() => {
  checkAndSaveVtidInRoute();
  trackEvents();
  trackPreferredAdvertiser();

  if (route.query?.access_token) {
    clearTokens();
    setTimeout(() => {
      initGateway();
    }, 500);
  }
});
</script>
<template>
  <div
    v-if="pageDataSuccess && pageData"
    :class="[pageData.color_mode === 'dark' ? 'dark' : '']"
    :style="{ backgroundColor: pageData.color_page_bg || '' }"
  >
    <main class="max-w-[1200px] px-4 2xl:px-0 mx-auto">
      <header
        class="flex items-center flex-row pt-10 md:pt-[72px] justify-between"
      >
        <img
          :alt="`${pageData.name} logo`"
          height="24"
          class="h-[24px] max-w-[168px] object-contain shrink-0"
          :src="advertiserMetadata.logo"
        />

        <a
          :href="`https://evangelist.gg?utm_source=${brandName.toLocaleLowerCase()}-landing&utm_campaign=${brandName.toLocaleLowerCase()}`"
          target="_blank"
          data-analytics-event-name="user_site_redirected"
          data-analytics-click-id="button_powered_by_clicked"
          class="text-white w-[26px] md:w-auto md:px-[11px] py-[7px] bg-gradient-to-b from-white/15 ring-[1px] ring-white/20 rounded-full flex gap-2 justify-center items-center"
        >
          <PoweredByEvangelistDark v-if="pageData.color_mode === 'dark'" />
          <PoweredByEvangelistLight v-else />
        </a>
      </header>

      <div class="grid mt-12 gap-4 md:mt-20 md:grid-cols-[1fr_475px]">
        <div class="lg:mt-9">
          <h1
            class="text-black dark:text-white text-3xl md:text-[56px] font-semibold leading-[120%]"
          >
            <span v-if="!pageData.landing_page_hero_title"
              >Join the {{ pageData.name }} <br />
              Evangelist Program</span
            >
            <span v-else v-html="pageData.landing_page_hero_title"></span>
          </h1>

          <p
            class="text-black dark:text-white mt-6 md:mt-12 max-w-[488px] text-xl md:text-2xl"
          >
            <span v-if="!pageData.landing_page_hero_description"
              >Create content about your {{ pageData.name }} experience on X and
              earn rewards based on quality and impact.</span
            >
            <span v-else v-html="pageData.landing_page_hero_description"></span>
          </p>

          <div class="md:flex mt-10 items-center gap-6">
            <div v-if="props.customUserName">
              <CustomUserBadge
                :user-name="props.customUserName"
                :user-profile-pic-url="props.customUserProfilePicUrl"
              />
            </div>
            <SigninButton
              :brand-name="brandName.toLowerCase()"
              class="flex gap-2 mt-6 md:mt-0 w-full md:w-auto justify-center md:justify-start px-4 py-2.5 text-white"
              :style="{
                backgroundColor: pageData.color_button_bg || '#000',
                borderRadius: pageData.button_border_radius || '32px',
              }"
            />
          </div>
        </div>

        <div class="mt-8 lg:mt-0 rounded-2xl">
          <img
            width="475"
            height="575"
            class="w-full h-full object-cover"
            :src="advertiserMetadata.cardImage"
            :alt="`Image of ${pageData.name}`"
          />
        </div>
      </div>

      <div
        v-if="campaignsSuccess && campaigns && campaigns.length > 0"
        class="border-t hidden md:block mt-[80px] dark:border-white/50 border-black/10"
      ></div>

      <div
        v-if="campaignsSuccess && campaigns && campaigns.length > 0"
        class="mt-10 md:mt-[80px]"
      >
        <h2 class="text-2xl text-black/50 dark:text-white/50">
          Explore active campaigns by {{ pageData.name }}
        </h2>

        <div class="w-full mt-10 space-y-4">
          <CampaignPreviewTile
            v-for="(campaign, i) in campaigns"
            :key="campaign.campaign_week_id"
            :title="campaign.title"
            :description="campaign.description_html"
            :imageUrl="advertiserMetadata.smallLogo"
            :endsAt="new Date(campaign.date_end)"
            :budget="campaign.total_budget"
            :usdConversionRate="campaign.usd_conversion_rate"
            :currency="campaign.budget_currency"
            :advertiserName="pageData.name"
            @click="
              signWithX({
                campaignId: campaign.campaign_week_id,
                campaignIndex: i,
              })
            "
          />
        </div>
      </div>
    </main>
    <Mission
      class="mt-[80px] pb-16"
      :pageData="pageData"
      :advertiserName="pageData.name"
    />
  </div>
</template>
